// Foundations
import { Injectable } from '@angular/core';
import {
  filter,
  map,
  switchMap,
  take,
  mergeMap,
  catchError,
} from 'rxjs/operators';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';

// Services
import {
  TranSafeIFrameHash,
  TranSafeIFrameParametersInput,
  CreateIFrameHashGQL,
  GetPreferredLocationGQL,
  GetPaymentCardsGQL,
  GetPaymentCardGQL,
  AddPaymentCardGQL,
  UpdatePaymentCardGQL,
  PaymentCard,
  DeletePaymentCardGQL,
  PayWithCreditCardGQL,
  OrderPayment,
} from '../services/graphql.service';
import { AuthService } from '../auth/auth.service';
import { ReservationType, BookingService } from './booking.service';

// Models
import {
  CreditCardTypeIconFilenames,
  CreditCardTypeIds,
  PaymentCreditCard,
  PaymentTypes,
} from '../models/payment/payment-card.model';

import * as lodash from 'lodash';
import * as moment from 'moment';
import { OrderRequestItem } from '../models/package/order-request.model';
import { PurchaseInfo } from '../models/package/purchase.model';

@Injectable({
  providedIn: 'root',
})
export class PaymentService {
  // #region -> Property <-
  // ----------

  tranSafeIFrameHash$: Observable<TranSafeIFrameHash>;
  tranSafeIFrameConfig: TranSafeIFrameParametersInput = {
    domain: `https://${window.location.host}`,
    cssUrl: `https://${window.location.host}/assets/css/iframe.css`,
    cardholderName: false,
    street: false,
    zip: true,
    expdateFormat: 'separate-selects',
    autoReload: true,
    autocomplete: false,
    includeSubmitButton: false,
  };

  paymentCreditCards: PaymentCreditCard[] = [];
  paymentCreditCards$ = new BehaviorSubject<PaymentCreditCard[]>([]);

  // ----------
  // #endregion

  /////////////////////////////

  // #region -> Constructor <-
  // ----------

  constructor(
    private authService: AuthService,
    private bookingService: BookingService,

    private createIFrameHash: CreateIFrameHashGQL,
    private addPaymentCardMutation: AddPaymentCardGQL,
    private updatePaymentCardMutation: UpdatePaymentCardGQL,
    private getPaymentCardsQuery: GetPaymentCardsGQL,
    private getPaymentCardQuery: GetPaymentCardGQL,
    private getPreferredLocationQuery: GetPreferredLocationGQL,
    private deletePaymentCardMutation: DeletePaymentCardGQL,
    private payWithCreditCardGQL: PayWithCreditCardGQL
  ) {}

  // ----------
  // #endregion

  /////////////////////////////

  // #region -> Method - TranSafe iFrame <-
  // ----------

  createTranSafeIFrameHash(useFranchiseId?: string) {
    //
    this.tranSafeIFrameHash$ = this.authService.accountId$.pipe(
      filter((accountId) => !!accountId),
      mergeMap((accountId) =>
        this.getPreferredLocationQuery.fetch({ accountId })
      ),
      take(1),
      map((res) => res.data.getPreferredLocation.id),
      switchMap((franchiseId) =>
        this.createIFrameHash
          .fetch({
            locationId: useFranchiseId ? useFranchiseId : franchiseId,
            parametersInput: this.tranSafeIFrameConfig,
          })
          .pipe(
            take(1),
            map((res: any) => {
              if (
                res.hasOwnProperty('data') &&
                res.data &&
                res.data.createIFrameHash
              ) {
                return res.data.createIFrameHash;
              }
              return undefined;
            })
          )
      )
    );
  }

  // ----------
  // #endregion

  /////////////////////////////

  // #region -> Method - Payment Method <-
  // ----------

  getPaymentCreditCardById(paymentCreditCardId: string) {
    //
    return this.getPaymentCardQuery
      .fetch({ paymentCardId: paymentCreditCardId })
      .pipe(
        map((res) => res.data.getPaymentCard),
        map((paymentCard) => {
          return this._processReturnedPaymentCard(paymentCard);
        })
      );
  }

  getPaymentCreditCards() {
    //
    return this.authService.accountId$.pipe(
      filter((accountId) => !!accountId),
      // Also fetch the "upcoming bookings"
      mergeMap((accountId) => {
        this.bookingService.fetchUpcomingBooks(accountId).subscribe();
        return this.getPaymentCardsQuery.fetch({ accountId });
      }),
      map((res) => res.data.getPaymentCards),
      take(1),
      map((paymentCards) => {
        //
        this.paymentCreditCards = [];
        if (Array.isArray(paymentCards)) {
          paymentCards.forEach((paymentCard) => {
            //
            const newPaymentCreditCard =
              this._processReturnedPaymentCard(paymentCard);
            this.paymentCreditCards.push(newPaymentCreditCard);
          });
        }

        // Cast the updates to the list
        this.paymentCreditCards$.next(this.paymentCreditCards);
      }),
      catchError((error) => {
        console.log(error);
        return of(error);
      })
    );
  }

  addPaymentCard(
    newPaymentCreditCardInfo: PaymentCreditCard,
    targetFranchiseId: string
  ): Observable<any> {
    //
    return this.addPaymentCardMutation
      .mutate({
        paymentCardInput: {
          franchise: targetFranchiseId,
          cardToken: newPaymentCreditCardInfo.cardToken,
          cardholderName: newPaymentCreditCardInfo.cardholderName,
          cardNickname: newPaymentCreditCardInfo.cardNickname,
          // cardExpirationDate: newPaymentCreditCardInfo.cardExpirationDate,
          cardType: newPaymentCreditCardInfo.cardType,
          isPrimary: newPaymentCreditCardInfo.isPrimary,
        },
      })
      .pipe(
        map((res) => res.data.addPaymentCard),
        map((paymentCard) => {
          const newPaymentCreditCard =
            this._processReturnedPaymentCard(paymentCard);
          this.paymentCreditCards.push(newPaymentCreditCard);
          return newPaymentCreditCard;
        })
      );
  }

  updatePaymentCard(paymentCreditCard: PaymentCreditCard): Observable<any> {
    //
    return this.updatePaymentCardMutation
      .mutate({
        paymentCardId: paymentCreditCard.id,
        paymentCardInput: {
          cardholderName: paymentCreditCard.cardholderName,
          cardExpirationDate: paymentCreditCard.cardExpirationDate,
          cardNickname: paymentCreditCard.cardNickname,
          isPrimary: paymentCreditCard.isPrimary,
        },
      })
      .pipe(
        map((ret) => ret.data.updatePaymentCard),
        map((paymentCard) => {
          const newPaymentCreditCard =
            this._processReturnedPaymentCard(paymentCard);
          return newPaymentCreditCard;
        })
      );
  }

  /**
   * Set a new "primary" method.
   *
   * NOTES:
   * - Based on the current logic, we just need to update the `isPrimary` flag on the targeted method.
   *  While the backend processes it, it will help clear the flag for ALL other method.
   */
  setPrimaryPaymentCard(
    newPaymentCreditCard: PaymentCreditCard
  ): Observable<any> {
    //
    return this.updatePaymentCardMutation.mutate({
      paymentCardId: newPaymentCreditCard.id,
      paymentCardInput: {
        isPrimary: true,
      },
    });
  }

  /**
   * Delete a payment method.
   *
   * NOTES:
   * - If the deleted one is the primary one, we need to find a new one to be "primary".
   * - However, if the deleted one is the "last one",
   *  and the user account currently have some "boarding" reservation, we cannot delete it.
   *
   */
  deletePaymentCard(paymentCreditCard: PaymentCreditCard): Observable<any> {
    //
    if (
      !paymentCreditCard.expired &&
      this.getValidPaymentCards().length === 1
    ) {
      // About to delete the "last" valid one.
      // Check if there is a "boarding" reservation.
      //  - If "yes", cannot delete
      if (this.bookingService.includeSpecificType([ReservationType.Boarding])) {
        // Cannot delete
        return throwError(
          'You cannot delete your last valid payment method due to an active boarding reservation.'
        );
      }
    }

    // if (this.getValidPaymentCards().length > 1 && paymentCreditCard.isPrimary) {
    if (this.paymentCreditCards.length > 1 && paymentCreditCard.isPrimary) {
      // Find a new one to be "primary".
      const newList = lodash.filter(
        this.paymentCreditCards,
        (card: PaymentCreditCard) => {
          // return card.id !== paymentCreditCard.id && !card.expired;
          return card.id !== paymentCreditCard.id;
        }
      );
      const newPrimary = newList[0];
      newPrimary.isPrimary = true;
      return this.updatePaymentCard(newPrimary).pipe(
        switchMap(() =>
          this.deletePaymentCardMutation.mutate({
            paymentCardId: paymentCreditCard.id,
          })
        )
      );
    } else {
      // Just delete this one
      return this.deletePaymentCardMutation.mutate({
        paymentCardId: paymentCreditCard.id,
      });
    }
  }

  hasPrimaryPaymentCard(): boolean {
    //
    for (const card of this.paymentCreditCards) {
      if (card.isPrimary) {
        return true;
      }
    }

    //
    return false;
  }

  /**
   * If it has a "non-expired" card
   */
  hasValidPaymentCard(): boolean {
    //
    for (const card of this.paymentCreditCards) {
      if (!card.expired) {
        return true;
      }
    }

    //
    return false;
  }

  getValidPaymentCards(): PaymentCreditCard[] {
    //
    return this.paymentCreditCards.filter((card) => {
      return !card.expired;
    });
  }

  /**
   * Get the "first" valid payment card
   *
   * Rules:
   * - If "primary" is valid, use it.
   * - If not, pick the "first" valid one from the "others" list
   * - Finally, return `null`
   */
  getFirstValidPaymentCard(): PaymentCreditCard {
    //
    const primary: PaymentCreditCard = lodash.find(this.paymentCreditCards, {
      isPrimary: true,
    });
    if (primary && !primary.expired) {
      return primary;
    }

    // Pick the "first" valid one from "others" list
    const otherValidList = lodash.filter(
      this.paymentCreditCards,
      (card: PaymentCreditCard) => {
        return card.id !== primary.id && !card.expired;
      }
    );
    if (otherValidList.length > 0) {
      return otherValidList[0];
    }

    //
    return null;
  }

  // ----------
  // #endregion

  /////////////////////////////

  // #region -> Method - Make Payment <-
  // ----------

  /**
   * Make a payment for an order.
   *
   * NOTES:
   * - The payment has two types.
   *  - Pay with "saved" card
   *  - Pay with temporary card.
   *
   * @param orderRequest The related order request.
   * @param purchaseInfo The related purchase info, including the payment info.
   */
  makePayment(orderRequest: OrderRequestItem, purchaseInfo: PurchaseInfo) {
    //
    if (purchaseInfo.newCard) {
      // Use temporary card
      return (
        this.payWithCreditCardGQL
          .mutate({
            orderId: orderRequest.orderId,
            remittancePayment: {
              paymentType: PaymentTypes.TransafeCreditCard,
              cardType: this._getCardTypeId(purchaseInfo.paymentCard.cardType),
              cardToken: purchaseInfo.paymentCard.cardToken,
              cardholderName: purchaseInfo.paymentCard.cardholderName,
              amount: orderRequest.calculatePriceInfo().total,
              tip: orderRequest.hasTip ? orderRequest.tipAmount : 0,
            },
          })
          // TODO: Confirm this logic works, we updated the api to include multiple order payments so
          // we need to make sure this didnt break
          .pipe(
            map((res) => res.data.payWithCreditCard),
            switchMap((order: OrderPayment[]) => {
              // Check if need to save this temporary card
              if (purchaseInfo.saveNewCard) {
                return this.addPaymentCard(
                  purchaseInfo.paymentCard,
                  orderRequest.selectedLocation.id
                ).pipe(map(() => order));
              } else {
                return of(order);
              }
            })
          )
      );
    } else {
      // Use saved card
      return this.payWithCreditCardGQL
        .mutate({
          orderId: orderRequest.orderId,
          remittancePayment: {
            paymentType: PaymentTypes.TransafeCreditCard,
            cardType: this._getCardTypeId(purchaseInfo.paymentCard.cardType),
            paymentCard: purchaseInfo.paymentCard.id,
            amount: orderRequest.calculatePriceInfo().total,
            tip: orderRequest.hasTip ? orderRequest.tipAmount : 0,
          },
        })
        .pipe(map((res) => res.data.payWithCreditCard));
    }
  }

  // ----------
  // #endregion

  /////////////////////////////

  // #region -> Method - Payment Methods Processing <-
  // ----------

  processPaymentCreditCardList(
    paymentCreditCardList: PaymentCreditCard[],
    expiredExcluded: boolean = false
  ): {
    primary: PaymentCreditCard;
    others: PaymentCreditCard[];
  } {
    if (
      Array.isArray(paymentCreditCardList) &&
      paymentCreditCardList.length < 1
    ) {
      // No record
      return {
        primary: null,
        others: [],
      };
    }

    /*
      Several rules:
      - If currently there is no "primary" one, chose the first one as it.
      - If having multiple "primary" ones, just pick the first one.
      - If there is any change to the "primary flag", update it.
    */
    let primary: PaymentCreditCard = lodash.find(paymentCreditCardList, {
      isPrimary: true,
    });
    if (!primary) {
      // No "primary" one, just pick the first "valid" one.
      const validCards = this.getValidPaymentCards();
      if (validCards.length > 0) {
        primary = validCards[0];
        // Update this change
        primary.isPrimary = true;
        this.updatePaymentCard(primary).subscribe();
      }
    }

    let others = lodash.filter(
      paymentCreditCardList,
      (card: PaymentCreditCard) => {
        return card.id !== primary.id;
      }
    );
    if (expiredExcluded) {
      others = lodash.filter(others, (card: PaymentCreditCard) => {
        return card.id !== primary.id && !card.expired;
      });
    }

    // Prep the return
    return {
      primary,
      others,
    };
  }

  // ----------
  // #endregion

  /////////////////////////////

  // #region -> Private Method <-
  // ----------

  private _processReturnedPaymentCard(
    paymentCard: PaymentCard
  ): PaymentCreditCard {
    //
    const newPaymentCreditCard: PaymentCreditCard = {
      id: paymentCard.id,
      cardToken: paymentCard.cardToken,
      cardAccount: `****${paymentCard.cardAccount.slice(-4)}`,
      cardExpirationDate: paymentCard.cardExpirationDate,
      cardType: paymentCard.cardType,
      cardholderName: paymentCard.cardholderName,
      isPrimary: paymentCard.isPrimary,
      cardNickname: paymentCard.cardNickname,
      // UI Related
      expMonth: paymentCard.cardExpirationDate
        ? String(moment(paymentCard.cardExpirationDate).month() + 1)
        : null,
      expYear: paymentCard.cardExpirationDate
        ? String(moment(paymentCard.cardExpirationDate).year() % 100)
        : null,
      isSelected: false,
      expired: this._checkExpirationState(paymentCard.cardExpirationDate),
      cardTypeIconFilename: this._getCardTypeIconFilename(paymentCard.cardType),
    };

    return newPaymentCreditCard;
  }

  private _getCardTypeIconFilename(cardType: string): string {
    //
    if (
      !lodash.isNil(cardType) &&
      CreditCardTypeIconFilenames.hasOwnProperty(cardType.toLowerCase())
    ) {
      return CreditCardTypeIconFilenames[cardType.toLowerCase()];
    }
    return CreditCardTypeIconFilenames.default;
  }

  /**
   * Check if a given "expiration date" has been expired.
   *
   * As a "credit card", it can only be considered as "expired" only if:
   * - The "current date" is "after" the
   *    "last day" of the card's "expiration month".
   *
   * @param expDate The expiration date to be checked.
   */
  private _checkExpirationState(expDate: Date): boolean {
    // For TESTING the "Expired Card" case
    // TODO - Can be removed if needed.
    // const expYear = String(moment(expDate).year() % 100);
    // if (expYear === '21') {
    //   return true;
    // }

    //
    const lastDayOfExpiredMonth = moment(expDate).endOf('month');
    // `true` - expired
    return moment().isAfter(lastDayOfExpiredMonth);
  }

  private _addPaymentCreditCardIntoList(paymentCreditCard: PaymentCreditCard) {
    // If the current list include an old record, remove it first
    if (lodash.find(this.paymentCreditCards, { id: paymentCreditCard.id })) {
      //
      lodash.remove(this.paymentCreditCards, {
        id: paymentCreditCard.id,
      });
    }

    // Then re-add it.
    this.paymentCreditCards.push(paymentCreditCard);
  }

  private _getCardTypeId(cardType: string): string {
    //
    if (CreditCardTypeIds.hasOwnProperty(cardType)) {
      return CreditCardTypeIds[cardType];
    }

    // Default to 'visa'
    return CreditCardTypeIds.visa;
  }

  // ----------
  // #endregion

  /////////////////////////////
}
